import { useMutation, useQueryClient } from '@tanstack/react-query';
import partition from 'lodash/partition';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { WorkloadScalingPolicy } from '@cast/types';

import { getWorkloadQueryKey } from 'hooks/queries/workload-optimization/useWorkloadQuery';
import { getWorkloadScalingPoliciesQueryKey } from 'hooks/queries/workload-optimization/useWorkloadScalingPoliciesQuery';
import { getWorkloadsQueryKey } from 'hooks/queries/workload-optimization/useWorkloadsQuery';

type UseBatchDeleteScalingPoliciesMutationArgs = {
  onSuccess?: (deletedCount: number, totalCount: number) => void;
  onError?: () => void;
};

const deletePolicy = async (policy: WorkloadScalingPolicy) => {
  return apiClient.workloadOptimization.deleteWorkloadScalingPolicy({
    clusterId: policy.clusterId,
    policyId: policy.id,
  });
};

export const useBatchDeleteScalingPoliciesMutation = ({
  onSuccess,
  onError,
}: UseBatchDeleteScalingPoliciesMutationArgs = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (policies: WorkloadScalingPolicy[]) => {
      return Promise.allSettled(policies.map(deletePolicy));
    },
    onSuccess: async (results, variables) => {
      await queryClient.invalidateQueries({
        queryKey: getWorkloadScalingPoliciesQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: getWorkloadQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: getWorkloadsQueryKey(),
      });

      const [fulfilled, rejected] = partition(
        results,
        (entry) => entry.status === 'fulfilled'
      );

      if (rejected.length > 0) {
        const firstError = rejected[0];
        notify.error(parseApiError(firstError.reason));
      }

      if (fulfilled.length > 0) {
        notify.success(
          `${fulfilled.length} scaling ${
            fulfilled.length === 1 ? 'policy' : 'policies'
          } successfully deleted`
        );
      }

      onSuccess?.(fulfilled.length, variables.length);
    },
    onError: (error) => {
      notify.error(parseApiError(error));
      onError?.();
    },
  });
};
